/**
 * Layout Store
 *
 * Manages resizable panel dimensions and persists them to localStorage.
 * Enforces constraints to ensure usable panel sizes.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Constraints
const MIN_LEFT_PANEL = 200;
const MAX_LEFT_PANEL = 600;
const MIN_RIGHT_PANEL = 200;
const MAX_RIGHT_PANEL = 500;
const MIN_OUTPUT_HEIGHT = 30; // Percentage
const MAX_OUTPUT_HEIGHT = 70; // Percentage
const MIN_TIMELINE_HEIGHT = 100;
const MAX_TIMELINE_HEIGHT = 400;
const MIN_SHELF_HEIGHT = 40;
const MAX_SHELF_HEIGHT = 200;
const MIN_CENTER_PANEL = 400; // Minimum width for center panel

interface LayoutStore {
  // Panel dimensions
  leftPanelWidth: number;
  rightPanelWidth: number;
  outputHeight: number; // Percentage of left panel height
  timelineHeight: number;
  shelfHeight: number;

  // Actions
  setLeftPanelWidth: (width: number) => void;
  setRightPanelWidth: (width: number) => void;
  setOutputHeight: (percentage: number) => void;
  setTimelineHeight: (height: number) => void;
  setShelfHeight: (height: number) => void;
  resetToDefaults: () => void;
}

const DEFAULT_STATE = {
  leftPanelWidth: 350,
  rightPanelWidth: 300,
  outputHeight: 35, // Output window is smaller, Script window is larger
  timelineHeight: 180,
  shelfHeight: 80,
};

/**
 * Clamp a value between min and max
 */
const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Layout Store
 * Manages resizable panel dimensions with persistence
 */
export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      setLeftPanelWidth: (width: number) => {
        const viewportWidth = window.innerWidth;
        const rightWidth = get().rightPanelWidth;
        const maxAllowed = viewportWidth - rightWidth - MIN_CENTER_PANEL;

        const constrained = clamp(
          width,
          MIN_LEFT_PANEL,
          Math.min(MAX_LEFT_PANEL, maxAllowed)
        );

        set({ leftPanelWidth: constrained });
      },

      setRightPanelWidth: (width: number) => {
        const viewportWidth = window.innerWidth;
        const leftWidth = get().leftPanelWidth;
        const maxAllowed = viewportWidth - leftWidth - MIN_CENTER_PANEL;

        const constrained = clamp(
          width,
          MIN_RIGHT_PANEL,
          Math.min(MAX_RIGHT_PANEL, maxAllowed)
        );

        set({ rightPanelWidth: constrained });
      },

      setOutputHeight: (percentage: number) => {
        const constrained = clamp(percentage, MIN_OUTPUT_HEIGHT, MAX_OUTPUT_HEIGHT);
        set({ outputHeight: constrained });
      },

      setTimelineHeight: (height: number) => {
        const constrained = clamp(height, MIN_TIMELINE_HEIGHT, MAX_TIMELINE_HEIGHT);
        set({ timelineHeight: constrained });
      },

      setShelfHeight: (height: number) => {
        const constrained = clamp(height, MIN_SHELF_HEIGHT, MAX_SHELF_HEIGHT);
        set({ shelfHeight: constrained });
      },

      resetToDefaults: () => {
        set(DEFAULT_STATE);
      },
    }),
    {
      name: 'nodebeat_layout_v2',
      version: 2,
      // Validate data on hydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Validate and clamp all values
          state.leftPanelWidth = clamp(
            state.leftPanelWidth,
            MIN_LEFT_PANEL,
            MAX_LEFT_PANEL
          );
          state.rightPanelWidth = clamp(
            state.rightPanelWidth,
            MIN_RIGHT_PANEL,
            MAX_RIGHT_PANEL
          );
          state.outputHeight = clamp(
            state.outputHeight,
            MIN_OUTPUT_HEIGHT,
            MAX_OUTPUT_HEIGHT
          );
          state.timelineHeight = clamp(
            state.timelineHeight,
            MIN_TIMELINE_HEIGHT,
            MAX_TIMELINE_HEIGHT
          );
          state.shelfHeight = clamp(
            state.shelfHeight || DEFAULT_STATE.shelfHeight,
            MIN_SHELF_HEIGHT,
            MAX_SHELF_HEIGHT
          );
        }
      },
    }
  )
);
